import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const TrendingProducts = ({ products }) => {
  // Move all hook calls to the top level - not inside conditionals
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [animationComplete, setAnimationComplete] = useState(false);
  
  // If no products provided, use empty array to avoid errors
  const trendingProducts = products?.slice(0, 3) || [];
  
  // Default product image - same placeholder as caisse / POS
  const defaultProductImage = "/logo.jpg";

  useEffect(() => {
    // Trigger animation completion after delay
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Format price with proper currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Handle image load event
  const handleImageLoaded = (productId) => {
    setLoadedImages(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
        when: "beforeChildren"
      }
    }
  };

  const productVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 12 
      }
    }
  };

  const titleVariants = {
    hidden: { y: -30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 200,
        damping: 20 
      }
    }
  };
  
  // If there are no products, render a placeholder or return null
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden bg-[#f6faf8] py-24">
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#a7f3d0]/10 rounded-full mix-blend-multiply filter blur-xl opacity-70"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#174540]/5 rounded-full mix-blend-multiply filter blur-xl opacity-70"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={titleVariants}
        >
          <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[#3d8361]">
            Sélection confort
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-[#0c2a27] inline-block relative">
            Coups de cœur
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-16 h-px bg-[#174540]/40"></span>
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 gap-y-14 sm:grid-cols-3 sm:gap-x-8 lg:gap-x-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {trendingProducts.map((product, index) => (
            <motion.div 
              key={product.id} 
              className="group relative"
              variants={productVariants}
              onMouseEnter={() => setHoveredProductId(product.id)}
              onMouseLeave={() => setHoveredProductId(null)}
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <div className="relative rounded-2xl overflow-hidden bg-white shadow-lg transform transition-all duration-500 hover:shadow-2xl">
                {/* Product tag - if it's new */}
                {index === 0 && (
                  <div className="absolute top-4 left-4 z-10">
                    <div className="bg-[#174540] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">
                      Nouveauté
                    </div>
                  </div>
                )}
                
                {/* Loading skeleton */}
                {!loadedImages[product.id] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-2xl"></div>
                )}
                
                {/* Main product image container */}
                <div 
                  className="aspect-w-4 aspect-h-5 sm:aspect-w-3 sm:aspect-h-4 overflow-hidden"
                  style={{ height: '320px' }} // Fixed height for all product images
                >
                  <motion.img
                    src={product.image || defaultProductImage}
                    alt={product.name}
                    className={`w-full h-full object-cover transform transition-all duration-700 ${hoveredProductId === product.id ? 'scale-110' : 'scale-100'} ${!loadedImages[product.id] ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={() => handleImageLoaded(product.id)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultProductImage;
                      handleImageLoaded(product.id);
                    }}
                  />
                  
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 ${hoveredProductId === product.id ? 'opacity-100' : ''}`}></div>
                </div>
                
                {/* Quick view button - appears on hover */}
                <AnimatePresence>
                  {hoveredProductId === product.id && (
                    <motion.div 
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link 
                        to={`/product/${product.id}/0`}
                        className="bg-white/90 backdrop-blur-sm text-[#174540] font-medium px-5 py-2.5 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300 hover:bg-white"
                      >
                        Voir le produit
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Product info */}
              <div className="mt-5">
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-[#174540] transition-colors duration-300">
                  <Link to={`/product/${product.id}/0`} className="inline-block">
                    {product.name}
                  </Link>
                </h3>
                
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xl font-semibold text-[#174540]">
                    {formatPrice(product.price)}
                  </p>
                  
                  <div className="flex items-center">
                    <div className="inline-flex bg-[#a7f3d0]/20 text-[#174540] rounded-full px-2.5 py-1">
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
                        </svg>
                        <span className="ml-1 text-xs font-semibold">4.9</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Flourish element */}
              <div className="absolute -right-3 -bottom-3 w-12 h-12 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <svg className="w-full h-full text-[#174540]/10" viewBox="0 0 100 100" fill="currentColor">
                  <path d="M0,0 L100,0 L100,100 L0,100 Z" />
                </svg>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom flourish */}
        <div className="mt-16 flex justify-center">
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-[#174540]/30 to-transparent"></div>
        </div>
        
        {/* Mobile view link */}
        <div className="mt-10 sm:hidden flex justify-center">
          <Link to="/products" className="group flex items-center text-base font-semibold bg-[#174540] text-white py-3 px-6 rounded-full shadow-lg hover:bg-[#0d2926] transition-all duration-300">
            <span>Explorer tous les produits</span>
            <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TrendingProducts;